import { z } from 'zod'
import { ContainerType } from './m1.ts'

/**
 * Chat "Frag dein Brain" (§12.2) and ⌘K mode "Fragen" (§11.1). Paths below /api/v1/chat.
 * Answers stream as Server-Sent Events (ChatEvent); every statement from the brain carries a numbered source.
 */

const id = z.uuid()
const isoDateTime = z.iso.datetime({ offset: true })

export const ChatContext = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }),
  z.object({ kind: z.literal('para'), para: ContainerType }),
  z.object({ kind: z.literal('container'), id, title: z.string().optional() }),
  z.object({ kind: z.literal('note'), id, title: z.string().optional() }),
])
export type ChatContext = z.infer<typeof ChatContext>

export const ChatCitation = z.object({
  /** the number the answer uses: [1], [2] … */
  n: z.number().int(),
  kind: z.enum(['note', 'task', 'container', 'meeting', 'file', 'source']),
  id,
  title: z.string(),
  /** app path, e.g. /n/<id>#chunk-<chunkId> */
  url: z.string(),
  snippet: z.string(),
  containerTitle: z.string().nullable(),
})
export type ChatCitation = z.infer<typeof ChatCitation>

export const ChatActionType = z.enum(['create_task', 'capture', 'append_note'])
export const ChatAction = z.object({
  id,
  type: ChatActionType,
  /** create_task: { title, containerId?, due?, description? } · capture: { text } · append_note: { noteId, markdown } */
  input: z.record(z.string(), z.unknown()),
  /** short German description for the confirmation card */
  label: z.string(),
  status: z.enum(['proposed', 'done', 'dismissed']),
  resultUrl: z.string().nullable().optional(),
})
export type ChatAction = z.infer<typeof ChatAction>

export const ChatMessage = z.object({
  id,
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  citations: z.array(ChatCitation),
  actions: z.array(ChatAction),
  meta: z.object({
    /** hits from local_only/no_ai containers that were not sent to Claude (§12.3) */
    hiddenProtected: z.number().int().optional(),
    model: z.string().optional(),
    error: z.string().optional(),
  }),
  createdAt: isoDateTime,
})
export type ChatMessage = z.infer<typeof ChatMessage>

export const ChatThread = z.object({
  id,
  title: z.string(),
  context: ChatContext,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type ChatThread = z.infer<typeof ChatThread>

// GET /chat/threads → { threads: ChatThread[] }
// POST /chat/threads → ChatThread
export const CreateChatThread = z.object({
  context: ChatContext.default({ kind: 'all' }),
  title: z.string().trim().max(200).default(''),
})
export type CreateChatThread = z.infer<typeof CreateChatThread>
// GET /chat/threads/:id → { thread: ChatThread, messages: ChatMessage[] }
// DELETE /chat/threads/:id → 204
// POST /chat/threads/:id/messages (SendChatMessage) → text/event-stream of ChatEvent
// POST /chat/ask (AskBrain) → creates a thread, then streams like /messages (⌘K "Fragen")
export const SendChatMessage = z.object({ content: z.string().trim().min(1).max(8000) })
export type SendChatMessage = z.infer<typeof SendChatMessage>
export const AskBrain = SendChatMessage.extend({ context: ChatContext.default({ kind: 'all' }) })
export type AskBrain = z.infer<typeof AskBrain>
// POST /chat/messages/:messageId/actions/:actionId/confirm (ConfirmChatAction) → ChatAction
// POST /chat/messages/:messageId/actions/:actionId/dismiss → ChatAction
export const ConfirmChatAction = z.object({ input: z.record(z.string(), z.unknown()).optional() })
export type ConfirmChatAction = z.infer<typeof ConfirmChatAction>
// POST /chat/threads/:id/note → { noteId } (save the conversation as a note, §12.2)

/** SSE events of a streamed answer (event name = type, data = JSON). */
export const ChatEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('thread'), thread: ChatThread, userMessage: ChatMessage }),
  /** progress, e.g. "Suche: Budget Nordwind" */
  z.object({ type: z.literal('status'), text: z.string() }),
  z.object({ type: z.literal('delta'), text: z.string() }),
  z.object({ type: z.literal('done'), message: ChatMessage }),
  z.object({ type: z.literal('error'), message: z.string() }),
])
export type ChatEvent = z.infer<typeof ChatEvent>
